import type { ResultOf } from '@graphql-typed-document-node/core';
import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { Button } from '@savvy-kit/components/button';
import { Separator } from '@savvy-kit/components/separator';
import { Text } from '@savvy-kit/foundations/text';
import { DateTime } from 'luxon';
import numeral from 'numeral';
import ItemDetailsRow from 'src/components/portfolio/views/item-details-row';
import { otherAccountDisplayName } from 'src/constants/account-link';
import { graphql, unmaskFragment } from 'src/gql';
import { isLinkedAccount } from 'src/utils/account';

import { accountSubtypeDisplayName } from '@savvy/deprecated-shared/utils/portfolio';

import LinkedAccountContent from '../../financial-account/linked-account-content';
import { TooltipWrapper } from '../../performance-report/performance-returns-details';

export const ViewAccountDialogDetailRowsFragment = graphql(/* GraphQL */ `
  fragment ViewAccountDialogDetailRows on Account {
    ...IsLinkedAccount
    ...LinkedAccountContent
    accountName
    availableCredit
    creditLimit
    displayBalance {
      amount
    }
    interestRate
    lastUpdatedAt
    name
    officialName
    providerAccount {
      providerName
    }
    providerAccountId
    subtype
    type
  }
`);

type Props = {
  readonly accountFrag: FragmentType<typeof ViewAccountDialogDetailRowsFragment>;
  readonly onEdit: Deprecated_HandleItemUpdate;
};
export function ViewAccountDialogDetailRows({ accountFrag, onEdit }: Props) {
  const account = unmaskFragment(ViewAccountDialogDetailRowsFragment, accountFrag);
  if (isLinkedAccount(account)) {
    return <LinkedAccountView account={account} onEdit={onEdit} />;
  }
  if (account.type === 'investment') {
    return <InvestmentAccountView account={account} />;
  } else if (account.type === 'depository') {
    return <DepositoryAccountView account={account} />;
  } else if (account.subtype === 'pledged_asset_line') {
    return <PledgedAssetLineView account={account} />;
  }
  return null;
}

function InvestmentAccountView({
  account,
}: {
  readonly account: ResultOf<typeof ViewAccountDialogDetailRowsFragment>;
}) {
  return (
    <div>
      <ItemDetailsRow detailLabel="Name" value={account.name} />
      <ItemDetailsRow
        detailLabel="Type"
        value={
          account.subtype && account.subtype !== 'other'
            ? accountSubtypeDisplayName(account.subtype)
            : // @ts-expect-error - FIXME: This is using the wrong type
              otherAccountDisplayName(account.type)
        }
      />
      <ItemDetailsRow detailLabel="Value" value={numeral(account.displayBalance.amount).format('$0,0.00')} />
    </div>
  );
}

function DepositoryAccountView({
  account,
}: {
  readonly account: ResultOf<typeof ViewAccountDialogDetailRowsFragment>;
}) {
  return (
    <div>
      <ItemDetailsRow detailLabel="Name" value={account.name} />
      <ItemDetailsRow detailLabel="Account type" value={accountSubtypeDisplayName(account.subtype || 'other')} />
      <ItemDetailsRow detailLabel="Balance" value={numeral(account.displayBalance.amount).format('$0,0.00')} />
    </div>
  );
}

function PledgedAssetLineView({ account }: { readonly account: ResultOf<typeof ViewAccountDialogDetailRowsFragment> }) {
  return (
    <>
      <div>
        <ItemDetailsRow detailLabel="Account type" value={accountSubtypeDisplayName(account.subtype)} />
        <ItemDetailsRow detailLabel="Last updated" value={DateTime.fromISO(account.lastUpdatedAt).toRelative()} />
      </div>
      <Separator className="my-8 bg-border-light" />

      <div>
        <ItemDetailsRow
          detailLabel="Outstanding loan"
          value={numeral(account.displayBalance.amount).format('$0,0.00')}
        />
        <ItemDetailsRow detailLabel="Available credit" value={numeral(account.availableCredit).format('$0,0.00')} />
        <ItemDetailsRow detailLabel="Credit limit" value={numeral(account.creditLimit).format('$0,0.00')} />
        <ItemDetailsRow
          detailLabel="Interest rate"
          value={
            <TooltipWrapper
              title={
                <Text>
                  SOFR stands for the Secured Overnight Financing Rate. It is published on each business day by the
                  Federal Reserve Bank of New York and can change at any time. The latest rate can be seen on the{' '}
                  <Button
                    variant="link"
                    className="h-auto p-0 align-baseline text-inherit underline"
                    render={
                      <a
                        href="https://www.newyorkfed.org/markets/reference-rates/sofr"
                        target="_blank"
                        rel="noreferrer"
                      />
                    }
                  >
                    Federal Reserve Bank of New York’s website
                  </Button>
                  .
                </Text>
              }
              placement="bottom"
            >
              <Text>{`SOFR + ${numeral(account.interestRate).format('0.0[00]')}% `}</Text>
            </TooltipWrapper>
          }
        />
      </div>
      <Text variant="body-sm" color="secondary" className="mt-8">
        This account information may be out of date. It is updated by the Savvy team once per week.
      </Text>
    </>
  );
}

function LinkedAccountView({
  account,
  onEdit,
}: {
  readonly account: ResultOf<typeof ViewAccountDialogDetailRowsFragment>;
  readonly onEdit: Deprecated_HandleItemUpdate;
}) {
  return (
    <>
      <div>
        <ItemDetailsRow detailLabel="Account name" value={account.officialName || account.accountName} />
        {account.subtype && (
          <ItemDetailsRow
            detailLabel="Account type"
            value={
              account.subtype !== 'other'
                ? accountSubtypeDisplayName(account.subtype)
                : // @ts-expect-error - FIXME: This is using the wrong type
                  otherAccountDisplayName(account.type)
            }
          />
        )}
        <ItemDetailsRow detailLabel="Institution" value={account.providerAccount?.providerName} />
        <ItemDetailsRow detailLabel="Value" value={numeral(account.displayBalance.amount).format('$0,0.00')} />
        <ItemDetailsRow detailLabel="Last updated" value={DateTime.fromISO(account.lastUpdatedAt).toRelative()} />
      </div>
      <LinkedAccountContent accountFrag={account} onEdit={onEdit} />
    </>
  );
}
